import assert from "assert";
import moment from "moment-timezone";
import { Array, Boolean, InstanceOf, Literal, Partial, Record, String, Union } from "runtypes";
import { clean } from "./clean";
import { PERSONABLE_COURSEROLE_COLLECTION } from "./collections";
import { CourseName } from "./course";
import { currentCourse, currentSemester } from "./current";
export const STUDENT_JOIN_OPEN_DAYS = 21;
export const STUDENT_JOIN_TOO_LATE = false;
export const STAFF_JOIN_OPEN_DAYS = 14;
export const StaffRoles = Union(
  Literal("assistant"),
  Literal("associate"),
  Literal("head"),
  Literal("ta"),
  Literal("headta"),
  Literal("instructor"),
  Literal("guest staff"),
  Literal("adviser")
);
export const StudentRoles = Union(Literal("student"));
export const OldRoles = Literal("developer").Or(Literal("captain"));
export const AllRoles = StaffRoles.Or(StudentRoles);
export const EveryRole = AllRoles.Or(OldRoles);
export const OrderedStaffRoles = [
  "instructor",
  "headta",
  "head",
  "ta",
  "associate",
  "assistant",
  "guest staff",
  "adviser"
];
export const HeadRoles = ["instructor", "head", "headta"];
export const NonAssistantRoles = ["instructor", "head", "headta", "ta", "associate"];
export const ScoreRoles = ["instructor", "head", "headta", "ta", "adviser"];
export const ProctorRoles = ["instructor", "head", "headta", "ta"];
export const QuizReviewRoles = ["instructor", "head", "headta"];
export const PaidRoles = ["head", "headta", "ta", "associate"];
export const BrowseRoles = ["guest staff", "adviser"];
export const RoleKind = Literal("course").Or(Literal("section"));
export const Role = CourseName.And(
  Record({
    kind: RoleKind,
    netID: String,
    staff: Boolean,
    role: AllRoles.Or(OldRoles),
    joined: Union(InstanceOf(Date), String)
  }).And(
    Partial({
      temporary: Boolean,
      title: String,
      CRN: String,
      left: Union(InstanceOf(Date), String)
    })
  )
);
export const isStaff = (roles) => roles.find(({ staff }) => staff) !== void 0;
export const staffRoles = (roles) => roles.filter(({ staff }) => staff);
export const isStudent = (roles) => roles.find(({ role }) => role === "student") !== void 0;
export const hasRole = (roles) => roles && roles.length > 0;
export const activeRoles = (roles) => roles.filter(({ left }) => left === void 0);
export function currentRoles(roles, currentSemester2) {
  return roles.filter(({ year, semester }) => year === currentSemester2.year && semester == currentSemester2.name);
}
export const hasActiveRole = (roles) => roles && activeRoles(roles).length > 0;
export const hasCurrentActiveRole = (roles, currentSemester2) => roles && currentRoles(activeRoles(roles), currentSemester2).length > 0;
export function currentStaffRole(roles, semester) {
  const currentStaffRoles = staffRoles(activeRoles(currentRoles(roles, semester)));
  if (currentStaffRoles.length === 0) {
    return void 0;
  }
  assert(currentStaffRoles.length === 1);
  const staffRole2 = currentStaffRoles[0];
  staffRole2.title = titleForRole(staffRole2.role);
  return staffRole2;
}
export function hasCurrentStaffRole(roles, semester) {
  const currentStaffRoles = staffRoles(activeRoles(currentRoles(roles, semester)));
  assert(currentStaffRoles.length <= 1);
  return currentStaffRoles.length === 1;
}
export function staffRole(roles) {
  const currentStaffRoles = staffRoles(activeRoles(roles));
  if (currentStaffRoles.length === 0) {
    return void 0;
  }
  assert(currentStaffRoles.length === 1);
  const staffRole2 = currentStaffRoles[0];
  staffRole2.title = titleForRole(staffRole2.role);
  return staffRole2;
}
export const Category = Union(
  Literal("student"),
  Literal("staff"),
  Literal("former staff"),
  Literal("former student"),
  Literal("alumni")
);
export function categoryFromRoles(roles, semester) {
  const current = activeRoles(currentRoles(roles, semester));
  if (isStaff(current)) {
    return "staff";
  } else if (isStudent(current)) {
    return "student";
  }
  const active = activeRoles(roles);
  if (isStaff(roles)) {
    return "former staff";
  } else if (isStudent(active)) {
    return "alumni";
  } else if (isStudent(roles)) {
    return "former student";
  }
}
export const MENTOR_MAPPING = {
  assistant: ["associate", "head", "instructor"],
  associate: ["head", "instructor"],
  ta: ["headta", "instructor"],
  headta: ["instructor"],
  head: ["instructor"]
};
const sortBy = (key) => {
  return (a, b) => a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0;
};
export const loadActiveRoles = async (db, filter, uniqueNetID = false) => {
  const allRoles = Array(Role).check(
    (await db.collection(PERSONABLE_COURSEROLE_COLLECTION).find({
      kind: "course",
      left: { $exists: false },
      ...filter
    }).sort({ netID: 1 }).toArray()).map((r) => clean(r))
  );
  if (!uniqueNetID) {
    return allRoles;
  }
  allRoles.sort(sortBy("temporary"));
  const byNetID = {};
  for (const role of allRoles) {
    byNetID[role.netID] = role;
  }
  return Object.values(byNetID).sort(sortBy("netID"));
};
export const loadAllRoles = async (db, filter) => {
  return Array(Role).check(
    (await db.collection(PERSONABLE_COURSEROLE_COLLECTION).find({
      kind: "course",
      ...filter
    }).sort({ netID: 1 }).toArray()).map((r) => clean(r))
  );
};
export const hasActiveRoleInCourse = async (db, netID, role = void 0, course = currentCourse) => {
  const activeRoles2 = await loadActiveRoles(db, {
    netID,
    temporary: { $exists: false },
    ...role && { role },
    ...course
  });
  return activeRoles2.length > 0;
};
const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";
const roleTitles = {
  instructor: "Instructor",
  head: "Head Tutor",
  headta: "Head Graduate Tutor",
  associate: "Tutor",
  assistant: "Assistant Tutor",
  ta: "Graduate Tutor",
  "guest staff": "Guest Staff",
  adviser: "Academic Adviser",
  student: "Student"
};
export const titleForRole = (role, plural = false) => {
  const title = roleTitles[role] ?? capitalize(role);
  if (!plural || title === "Guest Staff") {
    return title;
  } else {
    return `${title}s`;
  }
};
const ACCEPTING_ASSISTANTS = false;
export const getCanStaff = (roles, semester) => {
  if (semester !== currentSemester) {
    return { canStaff: false, explanation: "Wrong semester", contact: false };
  }
  if (categoryFromRoles(roles, semester) === "student") {
    return { canStaff: false, explanation: "You're a student!" };
  }
  if (!ACCEPTING_ASSISTANTS) {
    return {
      canStaff: false,
      explanation: `We're not recruiting Assistant Tutors for ${currentSemester.name}`,
      contact: false
    };
  }
  if (moment().isAfter(moment(currentSemester.start).add(STAFF_JOIN_OPEN_DAYS, "days"))) {
    return { canStaff: false, explanation: `Too late to join for ${currentSemester.name}`, contact: true };
  }
  if (!(categoryFromRoles(roles, semester) === "former staff" || categoryFromRoles(roles, semester) === "staff" || categoryFromRoles(roles, semester) === "alumni")) {
    return { canStaff: false, explanation: "Didn't take CS 124", contact: true };
  }
  return { canStaff: true };
};
//# sourceMappingURL=role.js.map
